import { useQuery } from '@tanstack/react-query';
import { Badge } from '@clay-run/glaze/badge';
import { useApi } from '../../../lib/api';
import { fetchJson } from '../../../lib/apiFetchJson';
import { WarningsResponseSchema, type Warning } from '@shared/flow/enrollment';

/**
 * Anti-double-flow badges (D3, U7's producer + this bead's consumer wiring).
 *
 * `GET /api/flow/queue/items/:id/warnings` (`src/services/flow/warnings.ts`)
 * shipped in U7 with no UI consumer — flagged as an explicit open question
 * in that bead's own notes ("should `.5`'s editor render the three
 * badges?"). Wired here, in the two places the plan doc names ("surface in
 * the picker/editor," docs/flow/flow-queuing-implementation-plan.md D3):
 * the queue's contact card (`QueueBody.tsx` `ContactRowMeta`, next to the
 * inline play picker) and the shared edit-in-place editor
 * (`EnrollmentItemEditor.tsx`) — the latter is the ONE component D2 reuses
 * in both the docked sidebar and the full-screen review, so mounting the
 * fetch there covers both surfaces without a second query.
 *
 * Non-blocking by construction (D3): rendered as informational pills, never
 * gating Queue/Send. Read-only, allowed under impersonation (no D4 guard on
 * this endpoint), so no `impersonating` prop is needed here.
 */

/** Tooltip for the "In flow" pill: names the source (Gong probe vs the lagging Salesforce flag) and the owner. */
export function inFlowTooltip(warning: Extract<Warning, { kind: 'in_flow' }>): string {
  const source = warning.source === 'salesforce' ? 'Salesforce' : 'Gong';
  const base = `${source}: ${warning.flowName ?? 'unknown flow'} (${warning.flowStatus ?? 'status unknown'})`;
  const note = warning.sourceNote ? ` ${warning.sourceNote}` : '';
  const owner = warning.ownerEmail ? `, owner ${warning.ownerEmail}` : '';
  return `${base}${note}${owner}`;
}

export function WarningBadges({
  itemId,
  contactEmail,
  suppressKinds,
  inline = false,
}: {
  itemId: number;
  contactEmail: string | null;
  /**
   * Warm Intro plan U11 (R9): the item editor mounts `RecentSendsBar`
   * alongside this component and passes `['recent_invite']` here so the
   * amber pill doesn't duplicate the bar's signal — `in_flow` is unaffected
   * and keeps rendering everywhere (R9: "stays everywhere"). `QueueBody`'s
   * row mount passes nothing (undefined), so its rows keep every pill
   * exactly as before this bead.
   *
   * `'cooloff'` is deliberately absent from this union: it was a pure
   * duplicate of `recent_invite` — `warnings.ts`'s `hasRecentPlayhouseSend`
   * pushed both from the exact same single condition, and no independent
   * cool-off-window check ever existed.
   */
  suppressKinds?: Array<'recent_invite'>;
  /**
   * Rail-glaze-parity FIX 1: `LeadRow` renders this component as a direct
   * sibling of the row's trailing badges/Send button (via `RailRow`'s
   * `trailing` slot) rather than stacked under the title, for a row whose
   * ONLY meta content is this pill — centering it against the title relies
   * on both slots being the same single-line height, which the default
   * `mt-1` (sized for "a line of text sits above me") would throw off by 4px.
   * `inline` drops that margin; every other caller (this component's
   * default) keeps it.
   */
  inline?: boolean;
}) {
  const { apiFetch } = useApi();
  const { data } = useQuery<Warning[]>({
    queryKey: ['flow-queue-warnings', itemId],
    queryFn: () => fetchJson(apiFetch, WarningsResponseSchema, `/api/flow/queue/items/${itemId}/warnings`),
    // Nothing on the client mutates warnings; cache-indefinitely (CLAUDE.md
    // caching rule) — no TTL, no invalidation path needed for a read-only
    // signal keyed on the item's own identity.
    staleTime: Infinity,
  });

  if (!data || data.length === 0) return null;

  // `Set<string>` rather than `Array<'recent_invite'>.includes` — `warning.kind`
  // is the wider `Warning['kind']` union (includes 'in_flow'/'contact_deleted'),
  // and a `Set<string>.has(string)` doesn't need a narrowing cast to check it.
  const suppressed = new Set<string>(suppressKinds ?? []);
  const visible = data.filter(w => !suppressed.has(w.kind));

  if (visible.length === 0) return null;

  // Warm Intro plan U9 (R4): a no-email item passes `contactEmail: null`
  // (`recent_invite` never fires for one — warnings.ts skips
  // `hasRecentInvite` on a null email — but `in_flow` still can).
  // Interpolating null straight into a template literal would render the
  // literal string "null"; drop the "for <email>" suffix entirely instead
  // of inventing a fallback identifier this component was never given.
  const forSuffix = contactEmail ? ` for ${contactEmail}` : '';

  return (
    <div className={inline ? 'flex flex-wrap items-center gap-1' : 'mt-1 flex flex-wrap items-center gap-1'}>
      {visible.map((warning: Warning) => {
        switch (warning.kind) {
          case 'contact_deleted':
            return (
              <Badge
                key="contact_deleted"
                color="red"
                size="xs"
                aria-label={`Contact deleted warning${forSuffix}`}
                tooltipContent="This contact was deleted or merged in Salesforce"
              >
                Contact deleted
              </Badge>
            );
          case 'in_flow':
            return (
              <Badge
                key="in_flow"
                color="purple"
                size="xs"
                aria-label={`In-flow warning${forSuffix}`}
                tooltipContent={inFlowTooltip(warning)}
              >
                In flow
              </Badge>
            );
          case 'recent_invite':
            return (
              <Badge
                key="recent_invite"
                color="teal"
                size="xs"
                aria-label={`Recent invite warning${forSuffix}`}
                tooltipContent="Invited via Playhouse in the last 30 days"
              >
                Recent invite
              </Badge>
            );
        }
      })}
    </div>
  );
}
